import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './App.css';
import Totem from './pages/Totem';
import AgendaPT from './pages/AgendaPT';
import AlumnoApp from './pages/AlumnoApp';
import ProfesorApp from './pages/ProfesorApp';
import { API_URL, normalizarProfesor } from './constants';

// ── Rutas standalone sin navbar de admin ────────────────────────────
const IS_ALUMNO_ROUTE   = window.location.pathname.startsWith('/alumno');
const IS_PROFESOR_ROUTE = window.location.pathname.startsWith('/profesor');

function App() {
  const [modulo, setModulo] = useState('totem');
  const [alumnos, setAlumnos]   = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [alumnoAEliminar, setAlumnoAEliminar] = useState(null);

  useEffect(() => {
    cargarDatos();
    // Refresca en segundo plano para reflejar cambios hechos desde otra
    // pestaña/dispositivo (ej: reserva hecha en la App del Alumno
    // apareciendo en Agenda PT) sin que el usuario tenga que recargar.
    const interval = setInterval(() => cargarDatos(true), 8000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarDatos = async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const [alumnosRes, profesoresRes, reservasRes] = await Promise.all([
        axios.get(`${API_URL}/alumnos`),
        axios.get(`${API_URL}/profesores`),
        axios.get(`${API_URL}/reservas`)
      ]);
      if (alumnosRes.data?.length > 0) setAlumnos(alumnosRes.data);
      setProfesores(profesoresRes.data);
      setReservas(reservasRes.data);
    } catch (err) {
      console.warn('API no disponible:', err.message);
    } finally {
      if (!silencioso) setLoading(false);
    }
  };

  const handleAgregarAlumno = async (nuevoAlumno) => {
    try {
      const res = await axios.post(`${API_URL}/alumnos`, nuevoAlumno);
      setAlumnos(prev => [...prev, res.data]);
      alert('✅ Alumno registrado exitosamente');
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleNuevaReserva = async (reserva) => {
    const reservaLocal = { id: Date.now(), ...reserva };
    setReservas(prev => [...prev, reservaLocal]);
    try {
      const res = await axios.post(`${API_URL}/reservas`, reserva);
      setReservas(prev => prev.map(r =>
        r.id === reservaLocal.id ? { ...res.data, alumno_nombre: reserva.alumno_nombre } : r
      ));
    } catch (err) {
      console.warn('Reserva guardada solo localmente:', err.message);
    }
  };

  const handleAgregarEntrenador = async (nuevoEnt) => {
    try {
      const res = await axios.post(`${API_URL}/profesores`, nuevoEnt);
      setProfesores(prev => [...prev, res.data]);
      return res.data;
    } catch (err) {
      alert('❌ Error al agregar entrenador: ' + (err.response?.data?.error || err.message));
      return null;
    }
  };

  const handleEliminarEntrenador = async (id) => {
    try {
      await axios.delete(`${API_URL}/profesores/${id}`);
      setProfesores(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      alert('❌ Error al eliminar entrenador: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleActualizarEntrenador = async (id, cambios) => {
    try {
      const res = await axios.put(`${API_URL}/profesores/${id}`, cambios);
      setProfesores(prev => prev.map(p => p.id === id ? res.data : p));
    } catch (err) {
      alert('❌ Error al actualizar entrenador: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelarReserva = async (reservaId) => {
    try {
      await axios.delete(`${API_URL}/reservas/${reservaId}`);
      setReservas(prev => prev.filter(r => r.id !== reservaId));
      alert('✅ Reserva cancelada');
    } catch (err) {
      alert('❌ Error: ' + err.message);
    }
  };

  const handleAprobarAlumno = async (id) => {
    try {
      const res = await axios.put(`${API_URL}/alumnos/${id}`, { revisado: true });
      setAlumnos(prev => prev.map(a => a.id === id ? res.data : a));
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const confirmarEliminarAlumno = async () => {
    const id = alumnoAEliminar?.id;
    setAlumnoAEliminar(null);
    if (!id) return;
    try {
      await axios.delete(`${API_URL}/alumnos/${id}`);
      setAlumnos(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRegistrarAcceso = async (alumnoId) => {
    try {
      await axios.post(`${API_URL}/acceso`, { alumno_id: alumnoId, tipo: 'ingreso' });
    } catch (err) {
      console.error('Error registrando acceso:', err);
    }
  };

  const entrenadores = useMemo(() => profesores.map(normalizarProfesor), [profesores]);
  const alumnosPendientes = alumnos.filter(a => a.origen === 'app' && !a.revisado);

  // ── Vista profesor: pantalla completa sin navbar ────────────────────
  if (IS_PROFESOR_ROUTE) {
    return (
      <div className="app">
        {loading && <div className="loading">Cargando...</div>}
        <ProfesorApp
          entrenadores={entrenadores}
          reservas={reservas}
          alumnos={alumnos}
          onCancelarReserva={handleCancelarReserva}
        />
      </div>
    );
  }

  // ── Vista alumno: pantalla completa sin navbar ──────────────────────
  if (IS_ALUMNO_ROUTE) {
    return (
      <div className="app">
        {loading && <div className="loading">Cargando...</div>}
        <AlumnoApp
          alumnos={alumnos}
          reservas={reservas}
          entrenadores={entrenadores}
          onNuevaReserva={handleNuevaReserva}
          onCancelarReserva={handleCancelarReserva}
        />
      </div>
    );
  }

  // ── Vista admin: tótem + agenda PT ─────────────────────────────────
  return (
    <div className="app">
      <nav className="navbar">
        <div className="navbar-logo">INFINITY <span style={{ color: 'var(--cyan)' }}>CENTER</span></div>
        <div className="navbar-tabs">
          <button className={`nav-tab ${modulo === 'totem' ? 'active' : ''}`} onClick={() => setModulo('totem')}>
            📱 Tótem de Acceso
          </button>
          <button className={`nav-tab ${modulo === 'admin' ? 'active' : ''}`} onClick={() => setModulo('admin')}>
            📅 Agenda PT
          </button>
        </div>
        <div className="navbar-badge">Infinity Center — Temuco</div>
      </nav>

      {alumnosPendientes.length > 0 && (
        <div style={{ background: 'rgba(255,200,0,.1)', borderBottom: '1px solid var(--yellow)', padding: '12px 28px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--yellow)', marginBottom: 8 }}>
            ⚠️ {alumnosPendientes.length} registro{alumnosPendientes.length !== 1 ? 's' : ''} nuevo{alumnosPendientes.length !== 1 ? 's' : ''} desde la app — pendiente{alumnosPendientes.length !== 1 ? 's' : ''} de revisión
          </div>
          {alumnosPendientes.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
              <div style={{ flex: 1, fontSize: 13 }}>
                <strong>{a.nombre} {a.apellido}</strong> — {a.rut} {a.telefono && `· ${a.telefono}`}
              </div>
              <button className="btn-modal-agenda primary" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => handleAprobarAlumno(a.id)}>✓ Aprobar</button>
              <button className="btn-modal-agenda danger" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => setAlumnoAEliminar(a)}>✕ Eliminar</button>
            </div>
          ))}
        </div>
      )}

      {alumnoAEliminar && (
        <div className="modal-overlay-agenda" onClick={() => setAlumnoAEliminar(null)}>
          <div className="modal-agenda" onClick={e => e.stopPropagation()}>
            <div className="modal-title-agenda">¿Eliminar este registro?</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>
              <strong style={{ color: 'var(--text)' }}>{alumnoAEliminar.nombre} {alumnoAEliminar.apellido}</strong> — {alumnoAEliminar.rut}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>Esta acción no se puede deshacer.</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn-modal-agenda" onClick={() => setAlumnoAEliminar(null)}>Cancelar</button>
              <button className="btn-modal-agenda danger" onClick={confirmarEliminarAlumno}>✕ Eliminar</button>
            </div>
          </div>
        </div>
      )}

      <main className="main-content">
        {loading && <div className="loading">Cargando datos...</div>}

        {modulo === 'totem' && (
          <Totem
            alumnos={alumnos}
            onNuevoAlumno={handleAgregarAlumno}
            onAcceso={handleRegistrarAcceso}
          />
        )}

        {modulo === 'admin' && (
          <AgendaPT
            alumnos={alumnos}
            entrenadores={entrenadores}
            reservas={reservas}
            onNuevaReserva={handleNuevaReserva}
            onCancelarReserva={handleCancelarReserva}
            onAgregarEntrenador={handleAgregarEntrenador}
            onEliminarEntrenador={handleEliminarEntrenador}
            onActualizarEntrenador={handleActualizarEntrenador}
          />
        )}
      </main>
    </div>
  );
}

export default App;
